import { documentDisplayName } from '../../utils/documentNames';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/authContext';
import { listLookup } from '../../api/masterData';
import {
  createStudentInvoice,
  deleteStudentDocument,
  downloadInvoicePdf,
  downloadStudentDocument,
  generateStudentDocumentPdf,
  getStudentDocumentChecklist,
  listStudentDocuments,
  listStudentInvoices,
  previewStudentDocumentUrl,
  uploadStudentDocument,
} from '../../api/students';
import { invalidateStudentDocumentViews, useDocumentReview } from './useDocumentReview';
import { RejectDocumentDialog } from './RejectDocumentDialog';
import { getStudentSignature, getStudentSignatureImage, saveStudentSignature } from '../../api/studentSignature';
import { StudentSignatureAdapter, StudentSignatureSection } from './StudentSignatureSection';
import { GenerateDocumentDialog } from './GenerateDocumentDialog';
import {
  DocumentChecklistStatus,
  DocumentTemplateKey,
  DocumentVerificationStatus,
  EDITABLE_DOCUMENT_TEMPLATE_KEYS,
  EditableDocumentTemplateKey,
} from '../../types/student';
import { downloadBlob } from '../../utils/downloadBlob';
import { AgreementHistoryCard } from './AgreementHistoryCard';
import { DocumentPreviewDialog, DocumentPreviewTarget } from '../../components/DocumentPreviewDialog';

const VERIFICATION_COLOR: Record<DocumentVerificationStatus, 'default' | 'success' | 'error'> = {
  PENDING: 'default',
  APPROVED: 'success',
  REJECTED: 'error',
};

/** Admissions document-requirement correction (Phase 60) — the Required
 * Documents card below and the upload dropdown's scoping both key off
 * this same checklist status, so they can never disagree. */
const CHECKLIST_COLOR: Record<DocumentChecklistStatus, 'success' | 'warning' | 'error' | 'default'> = {
  APPROVED: 'success',
  PENDING: 'warning',
  REJECTED: 'error',
  MISSING: 'default',
};

const checklistIcon = (status: DocumentChecklistStatus) => {
  switch (status) {
    case 'APPROVED':
      return <CheckCircleIcon fontSize="small" />;
    case 'PENDING':
      return <HourglassEmptyIcon fontSize="small" />;
    case 'REJECTED':
      return <CancelIcon fontSize="small" />;
    default:
      return <ErrorOutlineIcon fontSize="small" />;
  }
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isExpiringSoon = (expiryDate: string | null) => {
  if (!expiryDate) return false;
  const daysLeft = (new Date(expiryDate).getTime() - Date.now()) / (24 * 60 * 60 * 1000);
  return daysLeft <= 30;
};

/** Document PDF Templates correction — the four official IMC PDFs every
 * student can have generated from their existing CRM data. Label here is
 * cosmetic only; `templateKey` is what the backend generator keys off, and
 * the DocumentType name is what the generated row actually appears under in
 * the "Uploaded documents" checklist / Master Data (kept in sync there so
 * this list is never a second, parallel checklist). */
const GENERATED_TEMPLATES: Array<{ key: DocumentTemplateKey; label: string }> = [
  { key: 'STUDENT_APPLICATION_FORM', label: 'Student Application Form' },
  { key: 'HEALTH_DECLARATION', label: 'Health Declaration Form' },
  { key: 'UNIVERSITY_APPLICATION', label: 'University Application Form' },
  { key: 'ADMISSION_AGREEMENT', label: 'Admission Consultancy Services Agreement' },
];

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong') : 'Something went wrong';

export const StudentDocumentsTab = ({ studentId }: { studentId: number }) => {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canVerify = hasPermission('documents', 'verify');
  const canGenerate = hasPermission('documents', 'upload');
  const canCreateInvoice = hasPermission('payments', 'create');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documentTypeId, setDocumentTypeId] = useState<number | ''>('');
  const [expiryDate, setExpiryDate] = useState('');
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceCurrencyId, setInvoiceCurrencyId] = useState<number | ''>('');
  const [invoiceDescription, setInvoiceDescription] = useState('');
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  // Admission Officer document-review workflow (§7): rejection always goes
  // through this dialog — never an immediate reject-with-no-reason click —
  // since rejectionReason must be saved and shown to the counselor.
  const [rejectTarget, setRejectTarget] = useState<{ id: number; label: string } | null>(null);
  // Editable document fields (Phase 91): the University Application Form
  // and Agreement go through the edit → preview → signature → generate
  // dialog; the other templates still generate in one click.
  const [editingTemplate, setEditingTemplate] = useState<{ key: EditableDocumentTemplateKey; label: string } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [docActionError, setDocActionError] = useState<string | null>(null);
  const [previewTarget, setPreviewTarget] = useState<DocumentPreviewTarget | null>(null);

  const documentsQuery = useQuery({
    queryKey: ['student-documents', studentId],
    queryFn: () => listStudentDocuments(studentId),
  });
  const documentTypesQuery = useQuery({
    queryKey: ['lookup', '/master-data/document-types'],
    queryFn: () => listLookup('/master-data/document-types', { activeOnly: true }),
  });
  const checklistQuery = useQuery({
    queryKey: ['student-document-checklist', studentId],
    queryFn: () => getStudentDocumentChecklist(studentId),
  });
  const invoicesQuery = useQuery({
    queryKey: ['student-invoices', studentId],
    queryFn: () => listStudentInvoices(studentId),
  });
  const currenciesQuery = useQuery({
    queryKey: ['lookup', '/master-data/currencies'],
    queryFn: () => listLookup('/master-data/currencies', { activeOnly: true }),
    enabled: invoiceOpen,
  });

  // Every view of this student's documents — this tab, AgreementHistoryCard's
  // history query, the requirement and Final Documentation checklists, the
  // Timeline — since any action here (upload/delete/verify/generate) can
  // change what they show.
  const invalidateDocuments = () => invalidateStudentDocumentViews(queryClient, studentId);
  // Earlier versions (with their own decisions), on request.
  const historyQuery = useQuery({
    queryKey: ['student-documents-history', studentId],
    queryFn: () => listStudentDocuments(studentId, true),
    enabled: showHistory,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadStudentDocument(studentId, file, documentTypeId as number, expiryDate || undefined),
    onSuccess: () => {
      invalidateDocuments();
      setExpiryDate('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: number) => deleteStudentDocument(documentId),
    onSuccess: invalidateDocuments,
  });

  // The same review action (and endpoint) as the Final Documentation
  // Checklist, so both screens always agree.
  const verifyMutation = useDocumentReview(studentId, () => setRejectTarget(null));

  const generateMutation = useMutation({
    mutationFn: (templateKey: DocumentTemplateKey) => generateStudentDocumentPdf(studentId, templateKey),
    onSuccess: () => {
      invalidateDocuments();
      setGenerateError(null);
    },
    onError: (err) => setGenerateError(extractErrorMessage(err)),
  });

  const createInvoiceMutation = useMutation({
    mutationFn: () =>
      createStudentInvoice(studentId, {
        amount: Number(invoiceAmount),
        currencyId: invoiceCurrencyId as number,
        description: invoiceDescription || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-invoices', studentId] });
      setInvoiceOpen(false);
      setInvoiceAmount('');
      setInvoiceCurrencyId('');
      setInvoiceDescription('');
      setInvoiceError(null);
    },
    onError: (err) => setInvoiceError(extractErrorMessage(err)),
  });

  const selectedType = documentTypesQuery.data?.find((t) => t.id === documentTypeId);

  // Admissions document-requirement correction (Phase 60) — the upload
  // dropdown must only offer document types applicable to this student's
  // university/programme (never every global DocumentType, never a type
  // from an unrelated university), and must drop a required type once it
  // is APPROVED (a PENDING or REJECTED one stays offered, since a
  // re-upload is exactly how resubmission works). Falls back to the full
  // global list only when no course can be resolved at all.
  const checklistItems = checklistQuery.data?.items;
  const uploadableDocumentTypes = checklistItems
    ? checklistItems.filter((item) => item.status !== 'APPROVED').map((item) => ({ id: item.documentTypeId, name: item.documentTypeName }))
    : (documentTypesQuery.data ?? [])
        // Other Documents (Phase 103) are named per document — added from the
        // Final Checklist's "10. Other Documents", not from this list.
        .filter((t) => t.finalChecklistKey !== 'OTHER')
        .map((t) => ({ id: t.id as number, name: documentDisplayName(String(t.name)) }));

  const handleDownload = async (documentId: number) => {
    try {
      const { blob, filename } = await downloadStudentDocument(documentId);
      downloadBlob(blob, filename);
    } catch (err) {
      setDocActionError(extractErrorMessage(err));
    }
  };

  const handleDownloadInvoice = async (invoiceId: number, invoiceNumber: string) => {
    try {
      const blob = await downloadInvoicePdf(studentId, invoiceId);
      downloadBlob(blob, `${invoiceNumber}.pdf`);
    } catch (err) {
      setDocActionError(extractErrorMessage(err));
    }
  };

  const signatureAdapter: StudentSignatureAdapter = {
    queryKey: ['student-signature', studentId],
    getSignature: () => getStudentSignature(studentId),
    getSignatureImage: () => getStudentSignatureImage(studentId),
    saveSignature: (method, file) => saveStudentSignature(studentId, method, file),
  };

  const uploadedDocuments =
    (showHistory ? historyQuery.data : documentsQuery.data)?.filter((d) => d.source === 'UPLOADED') ?? [];
  const generatedByTemplate = new Map(
    (documentsQuery.data ?? []).filter((d) => d.source === 'GENERATED' && d.templateKey).map((d) => [d.templateKey, d]),
  );

  return (
    <Box sx={{ mt: 2 }}>
      <StudentSignatureSection adapter={signatureAdapter} />

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        Official generated documents
      </Typography>
      {docActionError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDocActionError(null)}>
          {docActionError}
        </Alert>
      )}
      {generateError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setGenerateError(null)}>
          {generateError}
        </Alert>
      )}
      <List dense sx={{ mb: 2 }}>
        {GENERATED_TEMPLATES.map(({ key, label }) => {
          const doc = generatedByTemplate.get(key);
          const isGenerating = generateMutation.isPending && generateMutation.variables === key;
          return (
            <ListItem
              key={key}
              divider
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  {doc && (
                    <>
                      <Tooltip title="View">
                        <IconButton
                          onClick={() =>
                            setPreviewTarget({
                              key: doc.id,
                              originalName: doc.originalName,
                              mimeType: doc.mimeType,
                              fetchBlob: async () => (await previewStudentDocumentUrl(doc.id)).blob,
                              onDownload: () => handleDownload(doc.id),
                            })
                          }
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton onClick={() => handleDownload(doc.id)}>
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {canGenerate && (
                    <Button
                      size="small"
                      variant={doc ? 'outlined' : 'contained'}
                      startIcon={doc ? <RefreshIcon /> : undefined}
                      disabled={isGenerating}
                      onClick={() =>
                        EDITABLE_DOCUMENT_TEMPLATE_KEYS.includes(key)
                          ? setEditingTemplate({ key: key as EditableDocumentTemplateKey, label })
                          : generateMutation.mutate(key)
                      }
                    >
                      {isGenerating ? 'Generating…' : doc ? 'Regenerate' : 'Generate'}
                    </Button>
                  )}
                </Stack>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{label}</span>
                    <Chip
                      size="small"
                      label={doc ? `Generated (v${doc.version})` : 'Not generated'}
                      color={doc ? 'success' : 'default'}
                    />
                  </Stack>
                }
                secondary={doc ? `${doc.templateVersion ?? ''} · ${new Date(doc.createdAt).toLocaleString()}`.trim() : undefined}
              />
            </ListItem>
          );
        })}
      </List>

      <AgreementHistoryCard studentId={studentId} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Payment documents — invoices</Typography>
        {canCreateInvoice && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setInvoiceOpen(true)}>
            New invoice
          </Button>
        )}
      </Stack>
      <List dense sx={{ mb: 2 }}>
        {invoicesQuery.data?.map((inv) => (
          <ListItem
            key={inv.id}
            divider
            secondaryAction={
              <Tooltip title="Download">
                <IconButton onClick={() => handleDownloadInvoice(inv.id, inv.invoiceNumber)}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            }
          >
            <ListItemText
              primary={`${inv.invoiceNumber} — ${inv.currency.symbol ?? inv.currency.code} ${inv.amount}`}
              secondary={`${inv.description ?? ''} · issued ${new Date(inv.issuedAt).toLocaleDateString()} by ${inv.issuedBy.firstName} ${inv.issuedBy.lastName}`}
            />
          </ListItem>
        ))}
        {invoicesQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No invoices yet.
          </Typography>
        )}
      </List>

      {checklistQuery.data?.summary && checklistItems && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Required Documents
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {checklistQuery.data.summary.approved}/{checklistQuery.data.summary.requiredTotal} approved
            {checklistQuery.data.summary.pendingApproval > 0 && ` · ${checklistQuery.data.summary.pendingApproval} pending approval`}
            {checklistQuery.data.summary.missing > 0 && ` · ${checklistQuery.data.summary.missing} missing`}
            {checklistQuery.data.summary.rejected > 0 && ` · ${checklistQuery.data.summary.rejected} rejected`}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            {checklistItems.map((item) => (
              <Chip
                key={item.documentTypeId}
                size="small"
                icon={checklistIcon(item.status)}
                label={item.isMandatory ? item.documentTypeName : `${item.documentTypeName} (optional)`}
                color={CHECKLIST_COLOR[item.status]}
                variant={item.status === 'MISSING' ? 'outlined' : 'filled'}
              />
            ))}
          </Stack>
          {(checklistQuery.data.summary.missing > 0 || checklistQuery.data.summary.rejected > 0) && (
            <Alert severity="warning">
              Missing Documents:{' '}
              {checklistItems
                .filter((i) => i.status === 'MISSING' || i.status === 'REJECTED')
                .map((i) => i.documentTypeName)
                .join(', ')}
            </Alert>
          )}
        </Box>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Uploaded documents</Typography>
        <FormControlLabel
          control={<Switch size="small" checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} />}
          label="Show earlier versions"
        />
      </Stack>
      {verifyMutation.isError && !rejectTarget && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => verifyMutation.reset()}>
          {extractErrorMessage(verifyMutation.error)}
        </Alert>
      )}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select
          label="Document type"
          size="small"
          sx={{ width: 220 }}
          value={documentTypeId}
          onChange={(e) => setDocumentTypeId(e.target.value ? Number(e.target.value) : '')}
        >
          {uploadableDocumentTypes.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        {Boolean(selectedType?.requiresExpiry) && (
          <TextField
            label="Expiry date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) uploadMutation.mutate(file);
            e.target.value = '';
          }}
        />
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          disabled={!documentTypeId || uploadMutation.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
        </Button>
      </Stack>

      <List>
        {uploadedDocuments.map((d) => (
          <ListItem
            key={d.id}
            divider
            secondaryAction={
              <>
                <Tooltip title="Preview">
                  <IconButton
                    onClick={() =>
                      setPreviewTarget({
                        key: d.id,
                        originalName: d.originalName,
                        mimeType: d.mimeType,
                        fetchBlob: async () => (await previewStudentDocumentUrl(d.id)).blob,
                        onDownload: () => handleDownload(d.id),
                      })
                    }
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton onClick={() => handleDownload(d.id)}>
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                {canVerify && d.isLatest && d.verificationStatus !== 'APPROVED' && (
                  <Tooltip title="Approve">
                    <IconButton
                      color="success"
                      onClick={() => verifyMutation.mutate({ documentId: d.id, status: 'APPROVED' })}
                    >
                      <CheckCircleIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {canVerify && d.isLatest && d.verificationStatus !== 'REJECTED' && (
                  <Tooltip title="Reject">
                    <IconButton
                      color="error"
                      onClick={() => {
                        verifyMutation.reset();
                        setRejectTarget({ id: d.id, label: documentDisplayName(d.documentType.name, d.customName) });
                      }}
                    >
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton onClick={() => deleteMutation.mutate(d.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            }
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{documentDisplayName(d.documentType.name, d.customName)}</span>
                  <Chip size="small" label={d.originalName} variant="outlined" />
                  {(d.version > 1 || !d.isLatest) && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={d.isLatest ? `Version ${d.version}` : `Version ${d.version} (replaced)`}
                    />
                  )}
                  <Chip size="small" label={d.verificationStatus} color={VERIFICATION_COLOR[d.verificationStatus]} />
                  {d.expiryDate && (
                    <Chip
                      size="small"
                      label={`Expires ${new Date(d.expiryDate).toLocaleDateString()}`}
                      color={isExpiringSoon(d.expiryDate) ? 'error' : 'default'}
                    />
                  )}
                </Stack>
              }
              secondary={
                <>
                  {`${formatSize(d.sizeBytes)} · uploaded by ${d.uploadedBy.firstName} ${d.uploadedBy.lastName} on ${new Date(d.createdAt).toLocaleDateString()}`}
                  {d.verificationStatus === 'REJECTED' && d.rejectionReason && (
                    <Typography component="span" variant="body2" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                      Reason: {d.rejectionReason}
                      {d.verifiedBy && ` — rejected by ${d.verifiedBy.firstName} ${d.verifiedBy.lastName}`}
                      {d.verifiedAt && ` on ${new Date(d.verifiedAt).toLocaleDateString()}`}
                    </Typography>
                  )}
                  {d.verificationStatus === 'APPROVED' && d.verifiedBy && (
                    <Typography component="span" variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                      Approved by {d.verifiedBy.firstName} {d.verifiedBy.lastName}
                      {d.verifiedAt && ` on ${new Date(d.verifiedAt).toLocaleDateString()}`}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        ))}
      </List>

      <RejectDocumentDialog
        target={rejectTarget}
        pending={verifyMutation.isPending}
        error={verifyMutation.isError ? extractErrorMessage(verifyMutation.error) : null}
        onCancel={() => setRejectTarget(null)}
        onConfirm={(reason) =>
          rejectTarget && verifyMutation.mutate({ documentId: rejectTarget.id, status: 'REJECTED', reason })
        }
      />

      {uploadedDocuments.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No documents uploaded yet.
        </Typography>
      )}

      <Dialog open={invoiceOpen} onClose={() => setInvoiceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {invoiceError && <Alert severity="error">{invoiceError}</Alert>}
            <TextField
              label="Amount"
              type="number"
              value={invoiceAmount}
              onChange={(e) => setInvoiceAmount(e.target.value)}
              fullWidth
            />
            <TextField
              select
              label="Currency"
              value={invoiceCurrencyId}
              onChange={(e) => setInvoiceCurrencyId(e.target.value ? Number(e.target.value) : '')}
              fullWidth
            >
              {currenciesQuery.data?.map((c) => (
                <MenuItem key={c.id as number} value={c.id as number}>
                  {String(c.code)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={invoiceDescription}
              onChange={(e) => setInvoiceDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!invoiceAmount || !invoiceCurrencyId || createInvoiceMutation.isPending}
            onClick={() => createInvoiceMutation.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentPreviewDialog target={previewTarget} onClose={() => setPreviewTarget(null)} />

      {editingTemplate && (
        <GenerateDocumentDialog
          open
          studentId={studentId}
          templateKey={editingTemplate.key}
          title={editingTemplate.label}
          signatureAdapter={signatureAdapter}
          onClose={() => setEditingTemplate(null)}
          onGenerated={(document) => {
            setEditingTemplate(null);
            invalidateDocuments();
            void handleDownload(document.id);
          }}
        />
      )}
    </Box>
  );
};
